import {
  VStack,
  Button,
  Box,
  Text,
  HStack,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { PaymentMethod } from "./payment.types";
import { io, Socket } from "socket.io-client";
import { useNavigate } from "react-router-dom";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { clearCart } from "../../store/cartSlice/cartSlice";
import { useAppDispatch } from "../../store/hooks/hooks";
import {
  rememberCartCheckoutPayment,
  forgetCartCheckoutPayment,
} from "../../utils/checkoutPaidCart";

const removeTrailingSlash = (value: string) => {
  return value.replace(/\/+$/, "");
};

const isLocalFrontend = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const hostname = window.location.hostname.toLowerCase();

  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "0.0.0.0"
  );
};

const getBackendBaseUrl = () => {
  if (isLocalFrontend()) {
    return removeTrailingSlash(
      String(
        process.env.REACT_APP_LOCAL_BACKEND_URL || "http://localhost:5000",
      ).trim(),
    );
  }

  return removeTrailingSlash(
    String(
      process.env.REACT_APP_BACKEND_URL ||
        process.env.REACT_APP_PUBLIC_API_URL ||
        "https://api.pickdengu.mw",
    ).trim(),
  );
};

const BACKEND_BASE_URL = getBackendBaseUrl();
const PAYMENTS_API = `${BACKEND_BASE_URL}/api/v1/payments`;
const SOCKET_URL = BACKEND_BASE_URL;

export type CreatedOrderPaymentDetails = {
  orderId: string;
  amount?: number;
  currency?: string;
};

type CreateOrderResult = string | CreatedOrderPaymentDetails | null;

interface PaymentCheckoutProps {
  amount: number;
  onCreateOrder: (chargeId?: string) => Promise<CreateOrderResult>;
  isLoading?: boolean;
  isDisabled?: boolean;
  whatsAppBusinessNumber?: string;
  shouldClearCart?: boolean;
  pendingPath?: (id: string) => string;
  successPath?: (id: string) => string;
  onPaymentInitialized?: (
    orderId: string,
    method: PaymentMethod,
  ) => Promise<void> | void;
  onPaymentConfirmed?: (
    orderId: string,
    method: PaymentMethod,
  ) => Promise<void> | void;
  onWhatsAppPaymentLinkGenerated?: () => Promise<void> | void;
}

type WhatsAppPaymentLinkNotification = {
  attempted?: boolean;
  sent?: boolean;
  skipped?: boolean;
  alreadySent?: boolean;
  reason?: string;
  orderId?: string;
  recipientPhone?: string;
  txRef?: string;
  checkoutUrl?: string;
  messageId?: string;
};

export const WHATSAPP_PAYMENT_LINK_SUCCESS_STORAGE_KEY =
  "pickdenguWhatsAppPaymentLinkSuccess";

const WHATSAPP_PAYMENT_LINK_SUCCESS_TTL_MS = 30 * 60 * 1000;

export type WhatsAppPaymentLinkSuccessPayload = {
  checkoutChannel: "whatsapp";
  orderId: string;
  txRef: string;
  checkoutUrl: string;
  amount: number;
  currency: string;
  businessWhatsAppNumber: string;
  recipientPhone?: string;
  paymentLinkSent: boolean;
  notificationReason?: string;
  createdAt: number;
  expiresAt: number;
};

const storeWhatsAppPaymentLinkSuccess = (
  payload: Omit<WhatsAppPaymentLinkSuccessPayload, "createdAt" | "expiresAt">,
) => {
  if (typeof window === "undefined") {
    return;
  }

  const createdAt = Date.now();

  window.sessionStorage.setItem(
    WHATSAPP_PAYMENT_LINK_SUCCESS_STORAGE_KEY,
    JSON.stringify({
      ...payload,
      createdAt,
      expiresAt: createdAt + WHATSAPP_PAYMENT_LINK_SUCCESS_TTL_MS,
    } satisfies WhatsAppPaymentLinkSuccessPayload),
  );
};

type StandardCheckoutInitializeResponse = {
  message?: string;
  status?: string;
  checkoutUrl?: string;
  checkout_url?: string;
  txRef?: string;
  tx_ref?: string;
  orderId?: string;
  checkoutGroupId?: string;
  orderCount?: number;
  amount?: number;
  currency?: string;
  whatsAppNotification?: WhatsAppPaymentLinkNotification;
  data?: {
    whatsAppNotification?: WhatsAppPaymentLinkNotification;
    event?: string;
    checkout_url?: string;
    tx_ref?: string;
    data?: {
      tx_ref?: string;
      currency?: string;
      amount?: number;
      mode?: string;
      status?: string;
    };
  };
};

const STANDARD_PAYMENT_METHOD: PaymentMethod = "card";

type CheckoutChannel = "web" | "whatsapp";

type InitializedStandardCheckout = {
  orderId: string;
  checkoutUrl: string;
  txRef: string;
  amount: number;
  currency: string;
  whatsAppNotification?: WhatsAppPaymentLinkNotification;
};

const CHECKOUT_CHANNEL_STORAGE_KEY = "pickdenguCheckoutChannel";
const CHECKOUT_CHANNEL_TTL_MS = 4 * 60 * 60 * 1000;

const normalizeCheckoutChannel = (
  value: string | null | undefined,
): CheckoutChannel | null => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();

  if (["whatsapp", "wa"].includes(normalized)) {
    return "whatsapp";
  }

  if (["web", "browser"].includes(normalized)) {
    return "web";
  }

  return null;
};

const storeCheckoutChannel = (channel: CheckoutChannel) => {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(
    CHECKOUT_CHANNEL_STORAGE_KEY,
    JSON.stringify({
      channel,
      expiresAt: Date.now() + CHECKOUT_CHANNEL_TTL_MS,
    }),
  );
};

const getStoredCheckoutChannel = (): CheckoutChannel | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const rawValue = window.sessionStorage.getItem(
      CHECKOUT_CHANNEL_STORAGE_KEY,
    );

    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue) as {
      channel?: string;
      expiresAt?: number;
    };

    if (
      !Number.isFinite(Number(parsed.expiresAt)) ||
      Number(parsed.expiresAt) <= Date.now()
    ) {
      window.sessionStorage.removeItem(CHECKOUT_CHANNEL_STORAGE_KEY);
      return null;
    }

    return normalizeCheckoutChannel(parsed.channel);
  } catch {
    window.sessionStorage.removeItem(CHECKOUT_CHANNEL_STORAGE_KEY);
    return null;
  }
};

const isWhatsAppUserAgent = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return /whatsapp/i.test(window.navigator.userAgent || "");
};

const getSafePaymentUrl = (value: string): string => {
  const paymentUrl = String(value || "").trim();

  if (!paymentUrl) {
    throw new Error("The payment link is missing.");
  }

  const parsedUrl = new URL(paymentUrl);

  if (!["https:", "http:"].includes(parsedUrl.protocol)) {
    throw new Error("The payment link is invalid.");
  }

  return parsedUrl.toString();
};

const getCheckoutChannel = (): CheckoutChannel => {
  if (typeof window === "undefined") {
    return "web";
  }

  const params = new URLSearchParams(window.location.search);

  const explicitChannel =
    normalizeCheckoutChannel(params.get("channel")) ||
    normalizeCheckoutChannel(params.get("source")) ||
    normalizeCheckoutChannel(params.get("checkoutSource"));

  if (explicitChannel) {
    storeCheckoutChannel(explicitChannel);
    return explicitChannel;
  }

  if (/^\/whatsapp(?:\/|$)/i.test(window.location.pathname)) {
    storeCheckoutChannel("whatsapp");
    return "whatsapp";
  }

  if (isWhatsAppUserAgent()) {
    storeCheckoutChannel("whatsapp");
    return "whatsapp";
  }

  return getStoredCheckoutChannel() || "web";
};

const WHATSAPP_BUSINESS_NUMBER_STORAGE_KEY = "pickdenguWhatsAppBusinessNumber";

const normalizeWhatsAppNumber = (value: unknown) =>
  String(value || "").replace(/[^\d]/g, "");

const storeWhatsAppBusinessNumber = (value: string) => {
  if (typeof window === "undefined") {
    return;
  }

  const normalized = normalizeWhatsAppNumber(value);

  if (!normalized) {
    return;
  }

  window.sessionStorage.setItem(
    WHATSAPP_BUSINESS_NUMBER_STORAGE_KEY,
    normalized,
  );
};

const getWhatsAppBusinessNumber = (explicitNumber?: string) => {
  const explicit = normalizeWhatsAppNumber(explicitNumber);

  if (explicit) {
    storeWhatsAppBusinessNumber(explicit);
    return explicit;
  }

  if (typeof window === "undefined") {
    return normalizeWhatsAppNumber(
      process.env.REACT_APP_WHATSAPP_BUSINESS_NUMBER ||
        process.env.REACT_APP_WHATSAPP_NUMBER ||
        "",
    );
  }

  const params = new URLSearchParams(window.location.search);

  const queryNumber = normalizeWhatsAppNumber(
    params.get("businessWhatsAppNumber") ||
      params.get("businessWhatsappNumber") ||
      params.get("whatsappBusinessNumber") ||
      params.get("businessWhatsApp") ||
      params.get("businessWhatsapp") ||
      params.get("businessPhone") ||
      params.get("businessNumber") ||
      "",
  );

  if (queryNumber) {
    storeWhatsAppBusinessNumber(queryNumber);
    return queryNumber;
  }

  const storedNumber = normalizeWhatsAppNumber(
    window.sessionStorage.getItem(WHATSAPP_BUSINESS_NUMBER_STORAGE_KEY) ||
      window.localStorage.getItem(WHATSAPP_BUSINESS_NUMBER_STORAGE_KEY) ||
      "",
  );

  if (storedNumber) {
    return storedNumber;
  }

  return normalizeWhatsAppNumber(
    process.env.REACT_APP_WHATSAPP_BUSINESS_NUMBER ||
      process.env.REACT_APP_WHATSAPP_NUMBER ||
      "",
  );
};

const getErrorMessage = (error: unknown, fallback = "Something went wrong") => {
  if (axios.isCancel?.(error)) {
    return "Request was cancelled.";
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    error.code === "ERR_CANCELED"
  ) {
    return "Request was cancelled.";
  }

  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data;

    if (
      responseData &&
      typeof responseData === "object" &&
      "message" in responseData &&
      typeof responseData.message === "string"
    ) {
      return responseData.message;
    }

    if (
      responseData &&
      typeof responseData === "object" &&
      "error" in responseData &&
      typeof responseData.error === "string"
    ) {
      return responseData.error;
    }

    if (typeof responseData === "string") {
      return responseData;
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

const getPaymentEventStatus = (data: unknown): string => {
  if (!data || typeof data !== "object") {
    return "";
  }

  const paymentData = data as Record<string, unknown>;

  return String(
    paymentData.status ||
      paymentData.paymentStatus ||
      paymentData.payment_status ||
      "",
  ).toLowerCase();
};

const getCheckoutUrlFromResponse = (
  response: StandardCheckoutInitializeResponse,
) => {
  return String(
    response.checkoutUrl ||
      response.checkout_url ||
      response.data?.checkout_url ||
      "",
  ).trim();
};

const getTxRefFromResponse = (response: StandardCheckoutInitializeResponse) => {
  return String(
    response.txRef ||
      response.tx_ref ||
      response.data?.tx_ref ||
      response.data?.data?.tx_ref ||
      "",
  ).trim();
};

const PaymentCheckoutTest: React.FC<PaymentCheckoutProps> = ({
  amount,
  onCreateOrder,
  isLoading = false,
  isDisabled = false,
  whatsAppBusinessNumber,
  shouldClearCart = true,
  pendingPath = (id: string) => `/checkout-pending/${id}`,
  successPath = (id: string) => `/checkout-success/${id}`,
  onPaymentInitialized,
  onPaymentConfirmed,
  onWhatsAppPaymentLinkGenerated,
}) => {
  const { theme } = useCustomTheme();
  const toast = useToast();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [orderId, setOrderId] = useState<string | null>(null);
  const [createdOrderPayment, setCreatedOrderPayment] =
    useState<CreatedOrderPaymentDetails | null>(null);
  const [processing, setProcessing] = useState(false);
  const [checkoutChannel] = useState<CheckoutChannel>(() =>
    getCheckoutChannel(),
  );
  const [businessWhatsAppNumber] = useState(() =>
    getWhatsAppBusinessNumber(whatsAppBusinessNumber),
  );
  const socketRef = useRef<Socket | null>(null);
  const cartClearedRef = useRef(false);

  const clearCartSafely = () => {
    if (!shouldClearCart || cartClearedRef.current) {
      return;
    }

    dispatch(clearCart());
    cartClearedRef.current = true;
    if (orderId) {
      forgetCartCheckoutPayment(orderId);
    }
  };

  const clearCartAndNavigate = (path: string) => {
    clearCartSafely();
    navigate(path);
  };

  useEffect(() => {
    if (!orderId) {
      return;
    }

    socketRef.current?.disconnect();

    socketRef.current = io(SOCKET_URL, {
      transports: ["websocket"],
    });

    const channel = `payment-${orderId}`;

    const handlePaymentEvent = async (data: unknown) => {
      const status = getPaymentEventStatus(data);

      const paymentSucceeded =
        status === "paid" ||
        status === "success" ||
        status === "successful" ||
        status === "completed";

      if (paymentSucceeded) {
        try {
          if (onPaymentConfirmed) {
            await onPaymentConfirmed(orderId, STANDARD_PAYMENT_METHOD);
          }

          toast({
            title: "Payment confirmed",
            description: "Your PayChangu payment was received successfully.",
            status: "success",
            duration: 4000,
            isClosable: true,
          });

          clearCartAndNavigate(successPath(orderId));
        } catch (error: unknown) {
          console.error("onPaymentConfirmed error:", error);

          toast({
            title: "Payment confirmed but finalization failed",
            description:
              error instanceof Error
                ? error.message
                : "The payment was received, but the follow-up actions could not be completed.",
            status: "error",
            duration: 6000,
            isClosable: true,
          });
        }

        return;
      }

      const paymentFailed =
        status === "cancelled" ||
        status === "canceled" ||
        status === "failed" ||
        status === "declined";

      if (paymentFailed) {
        const paymentData =
          data && typeof data === "object"
            ? (data as Record<string, unknown>)
            : {};

        toast({
          title: "Payment not completed",
          description:
            typeof paymentData.message === "string"
              ? paymentData.message
              : "The payment was cancelled or failed. Please try again.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      }
    };

    socketRef.current.on(channel, handlePaymentEvent);

    socketRef.current.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
    });

    return () => {
      socketRef.current?.off(channel, handlePaymentEvent);
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [
    orderId,
    navigate,
    toast,
    successPath,
    onPaymentConfirmed,
    dispatch,
    shouldClearCart,
  ]);

  const getOrCreateOrderPayment = async () => {
    if (createdOrderPayment?.orderId) {
      return createdOrderPayment;
    }

    if (orderId) {
      return {
        orderId,
        amount,
        currency: "MWK",
      };
    }

    const result = await onCreateOrder();

    if (!result) {
      toast({
        title: "Order creation failed",
        description: "Could not create the order before payment.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });

      return null;
    }

    const paymentDetails: CreatedOrderPaymentDetails =
      typeof result === "string"
        ? {
            orderId: result,
            amount,
            currency: "MWK",
          }
        : {
            orderId: String(result.orderId || "").trim(),
            amount: Number(result.amount || amount || 0),
            currency: String(result.currency || "MWK")
              .trim()
              .toUpperCase(),
          };

    if (!paymentDetails.orderId) {
      throw new Error("The created order ID was not returned.");
    }

    setOrderId(paymentDetails.orderId);
    setCreatedOrderPayment(paymentDetails);

    return paymentDetails;
  };

  const initializeStandardCheckout = async (
    paymentDetails: CreatedOrderPaymentDetails,
    shouldRedirect: boolean,
  ): Promise<InitializedStandardCheckout> => {
    const resolvedOrderId = paymentDetails.orderId;
    const roundedAmount = Math.ceil(
      Number(paymentDetails.amount || amount || 0),
    );

    const paymentCurrency = String(paymentDetails.currency || "MWK")
      .trim()
      .toUpperCase();

    if (!Number.isFinite(roundedAmount) || roundedAmount <= 0) {
      throw new Error("The payment amount must be greater than zero.");
    }

    const response = await axios.post<StandardCheckoutInitializeResponse>(
      `${PAYMENTS_API}/standard/initialize`,
      {
        orderId: resolvedOrderId,
        amount: roundedAmount,
        currency: paymentCurrency || "MWK",
        checkoutChannel,
        channel: checkoutChannel,
        source: checkoutChannel,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const responseData = response.data || {};
    const checkoutUrl = getCheckoutUrlFromResponse(responseData);
    const txRef = getTxRefFromResponse(responseData);

    if (!checkoutUrl) {
      throw new Error("PayChangu Standard Checkout URL was not returned.");
    }

    if (!txRef) {
      throw new Error("PayChangu transaction reference was not returned.");
    }

    if (onPaymentInitialized) {
      await onPaymentInitialized(resolvedOrderId, STANDARD_PAYMENT_METHOD);
    }

    sessionStorage.setItem(
      "pickdenguPendingPayment",
      JSON.stringify({
        orderId: resolvedOrderId,
        txRef,
        amount: roundedAmount,
        currency: paymentCurrency || "MWK",
        checkoutUrl,
        checkoutChannel,
        createdAt: Date.now(),
        pendingPath: pendingPath(resolvedOrderId),
        successPath: successPath(resolvedOrderId),
      }),
    );

    if (shouldClearCart) {
      rememberCartCheckoutPayment(resolvedOrderId);
    }

    if (shouldRedirect) {
      // Payment initialization is not payment confirmation. Keep the cart.
      toast({
        title: "Redirecting to secure checkout",
        description:
          "You will complete your payment on the PayChangu checkout page.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });

      window.location.assign(checkoutUrl);
    }

    return {
      orderId: resolvedOrderId,
      checkoutUrl,
      txRef,
      amount: roundedAmount,
      currency: paymentCurrency || "MWK",
      whatsAppNotification:
        responseData.whatsAppNotification ||
        responseData.data?.whatsAppNotification,
    };
  };

  const handleStandardPayment = async () => {
    if (processing || isLoading || isDisabled) {
      return;
    }

    try {
      setProcessing(true);

      const paymentDetails = await getOrCreateOrderPayment();

      if (!paymentDetails) {
        return;
      }

      /*
       * Each module keeps its own fee and pricing logic. The module returns
       * the created order ID together with the final total from the order
       * creation response, and that exact total initializes PayChangu.
       */
      const initializedCheckout = await initializeStandardCheckout(
        paymentDetails,
        checkoutChannel !== "whatsapp",
      );

      if (checkoutChannel === "whatsapp") {
        const safePaymentUrl = getSafePaymentUrl(
          initializedCheckout.checkoutUrl,
        );

        if (onWhatsAppPaymentLinkGenerated) {
          await onWhatsAppPaymentLinkGenerated();
        }

        /*
         * A WhatsApp payment link is not a paid transaction. Store the link
         * result and navigate without removing cart contents. The cart may
         * be cleared later only after the backend confirms payment.
         */
        const notification = initializedCheckout.whatsAppNotification;

        const paymentLinkWasSent =
          notification?.sent === true || notification?.alreadySent === true;

        storeWhatsAppPaymentLinkSuccess({
          checkoutChannel: "whatsapp",
          orderId: initializedCheckout.orderId,
          txRef: initializedCheckout.txRef,
          checkoutUrl: safePaymentUrl,
          amount: initializedCheckout.amount,
          currency: initializedCheckout.currency,
          businessWhatsAppNumber,
          recipientPhone: notification?.recipientPhone,
          paymentLinkSent: paymentLinkWasSent,
          notificationReason: notification?.reason,
        });

        // Keep the cart while the recipient has not yet completed payment.
        navigate(successPath(initializedCheckout.orderId), {
          replace: true,
          state: {
            whatsappPaymentLinkCreated: true,
            orderId: initializedCheckout.orderId,
          },
        });
      }
    } catch (error: unknown) {
      console.error("PAYCHANGU STANDARD CHECKOUT ERROR:", error);

      if (axios.isAxiosError(error)) {
        console.error(
          "PAYCHANGU STANDARD CHECKOUT RESPONSE:",
          error.response?.data,
        );

        console.error(
          "PAYCHANGU STANDARD CHECKOUT REQUEST URL:",
          error.config?.url,
        );
      }

      const message = getErrorMessage(
        error,
        "PayChangu Standard Checkout could not be started.",
      );

      const normalizedMessage = message.toLowerCase();

      const isCancelled =
        normalizedMessage.includes("cancel") ||
        normalizedMessage.includes("cancelled") ||
        normalizedMessage.includes("canceled");

      toast({
        title: isCancelled
          ? "Payment cancelled"
          : "Payment could not be started",
        description: message,
        status: isCancelled ? "warning" : "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setProcessing(false);
    }
  };

  const displayAmount = Math.ceil(Number(amount || 0));
  const displayCurrency = "MWK";
  const isWhatsAppCheckout = checkoutChannel === "whatsapp";
  const cardsBorderRadii = "8px";

  return (
    <>
      <VStack
        spacing={4}
        p={{ base: 4, md: 5 }}
        bg={theme.surface}
        borderRadius={cardsBorderRadii}
        alignItems="stretch"
        w="100%"
        border="1px solid"
        borderColor={theme.border}
        boxShadow={theme.shadowSm}
      >
        <Box
          p={4}
          borderRadius={cardsBorderRadii}
          bg={theme.surface}
          border="1px solid"
          borderColor={theme.border}
        >
          <HStack justify="space-between" align="center" gap={4}>
            <Box>
              <Text color={theme.textMuted} fontSize="sm">
                Amount to pay
              </Text>

              <Text color={theme.text} fontSize="xl" fontWeight="900">
                {displayCurrency} {Number(displayAmount).toLocaleString()}
              </Text>
            </Box>
          </HStack>
        </Box>

        {isLoading ? (
          <Box
            p={4}
            borderRadius={cardsBorderRadii}
            bg={theme.surfaceAlt}
            border="1px solid"
            borderColor={theme.border}
          >
            <HStack spacing={3} align="flex-start">
              <Spinner
                mt={0.5}
                size="sm"
                thickness="3px"
                speed="0.65s"
                color={theme.primary}
                emptyColor={theme.border}
              />

              <Box>
                <Text color={theme.text} fontWeight="800">
                  Loading warehouses
                </Text>

                <Text color={theme.textMuted} fontSize="sm" mt={1}>
                  Seller dispatch and pickup details are being checked. Payment
                  will be enabled when they are ready.
                </Text>
              </Box>
            </HStack>
          </Box>
        ) : (
          <Button
            onClick={handleStandardPayment}
            isLoading={processing}
            loadingText={
              isWhatsAppCheckout
                ? "Sending payment link"
                : "Opening secure checkout"
            }
            isDisabled={
              processing || isDisabled || Number(displayAmount || 0) <= 0
            }
            w="100%"
            h="auto"
            px={6}
            py={4}
            whiteSpace="nowrap"
            textAlign="center"
            lineHeight="1.3"
            overflow="hidden"
            textOverflow="ellipsis"
            fontSize={{ base: "sm", md: "md" }}
            color={theme.textOnPrimary}
            bgImage={theme.gradients?.primaryButton || theme.primary}
            _hover={{
              bgImage:
                theme.gradients?.primaryButtonHover || theme.primaryHover,
            }}
            boxShadow={theme.shadowSm}
            borderRadius={cardsBorderRadii}
            fontWeight="900"
          >
            {isWhatsAppCheckout ? "Get Payment Link" : "Continue to Pay"} —{" "}
            {displayCurrency} {Number(displayAmount).toLocaleString()}
          </Button>
        )}

        <Text color={theme.textSubtle} fontSize="xs" textAlign="center">
          {isWhatsAppCheckout
            ? "The secure payment link is sent to the WhatsApp number saved on the order."
            : "You will be redirected to the secure checkout page to complete your payment."}
        </Text>
      </VStack>
    </>
  );
};

export default PaymentCheckoutTest;
